import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, of, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { SelectionItem } from '../../../core/component/select/select.component';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { AdjustmentKind, AdjustmentKindToString, RentalObjectCategory, RentalObjectCategoryToString } from '../../model/const/enums';
import { RentalObject } from '../../model/rental-object';
import { RentalObjectType } from '../../model/rental-object-type';
import { BuildingService } from '../../service/data/building.service';
import { EntranceService } from '../../service/data/entrance.service';
import {
  MissingRentalObjectCategoryArticle,
  RentalObjectCategoryService,
} from '../../service/data/rental-object-category.service';
import { RentalObjectTypeService } from '../../service/data/rental-object-type.service';

@Component({
  selector: 'app-change-rental-object-category-dialog',
  templateUrl: './change-rental-object-category-dialog.component.html',
  styleUrls: ['./change-rental-object-category-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChangeRentalObjectCategoryDialogComponent implements OnInit, OnDestroy {
  @Input() dataContext: RentalObject;

  form: FormGroup;
  categories: SelectionItem<RentalObjectCategory>[] = [];
  rentalObjectTypes: SelectionItem<string>[] = [];
  buildings: SelectionItem<string>[] = [];
  entrances: SelectionItem<string>[] = [];
  showBuilding = false;
  showEntrance = false;
  working = false;
  missingArticles: MissingRentalObjectCategoryArticle[] = [];

  private readonly subscriptions = new Subscription();
  private entranceSubscription: Subscription;

  constructor(
    private readonly dialogService: ActionWindowService,
    private readonly categoryService: RentalObjectCategoryService,
    private readonly rentalObjectTypeService: RentalObjectTypeService,
    private readonly buildingService: BuildingService,
    private readonly entranceService: EntranceService,
    private readonly messageService: MessageService,
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder,
    private readonly fcf: FormControlExFactory
  ) {}

  ngOnInit(): void {
    const currentCategory = Number(this.dataContext.category) as RentalObjectCategory;
    this.categories = [
      RentalObjectCategory.Dwelling,
      RentalObjectCategory.Premises,
      RentalObjectCategory.Parking,
      RentalObjectCategory.Other,
    ]
      .filter(category => category !== currentCategory)
      .map(category => new SelectionItem(this.translate.instant(RentalObjectCategoryToString(category)), category));

    this.form = this.fb.group({
      category: this.fcf.createMandatory('COMMON.RENTAL_OBJECT_CATEGORY', null),
      rentalObjectTypeId: this.fcf.createMandatory('RENTAL_OBJECT_CATEGORY.TYPE', null),
      buildingId: this.fcf.create('DWELLING_FORM.BUILDING_LABEL', this.dataContext.buildingId),
      entranceId: this.fcf.create('ENTRANCE.ENTRANCE', this.dataContext.entranceId),
    });

    this.subscriptions.add(
      this.form.controls['category'].valueChanges
        .pipe(
          tap(category => this.onCategoryChanged(category)),
          switchMap(category =>
            category === null || category === undefined ? of([] as RentalObjectType[]) : this.rentalObjectTypeService.getByCategory(category)
          )
        )
        .subscribe(types => (this.rentalObjectTypes = types.map(type => new SelectionItem(type.name, type.id))))
    );

    this.subscriptions.add(this.form.controls['buildingId'].valueChanges.subscribe(id => this.onBuildingChanged(id)));
    this.subscriptions.add(
      this.form.controls['rentalObjectTypeId'].valueChanges.subscribe(() => (this.missingArticles = []))
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  cancel(): void {
    this.dialogService.close();
  }

  onSubmit(): void {
    if (this.working || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.changeCategory();
  }

  adjustmentTranslation(adjustment: AdjustmentKind): string {
    return AdjustmentKindToString(adjustment);
  }

  categoryTranslation(category: RentalObjectCategory): string {
    return RentalObjectCategoryToString(category);
  }

  private changeCategory(): void {
    this.working = true;
    this.missingArticles = [];
    const value = this.form.value;
    this.categoryService
      .changeCategory({
        rentalObjectId: this.dataContext.id,
        rentalObjectTypeId: value.rentalObjectTypeId,
        buildingId: value.buildingId,
        entranceId: value.entranceId,
      })
      .pipe(
        finalize(() => (this.working = false)),
        catchError(error => {
          this.messageService.showError(error);
          return EMPTY;
        })
      )
      .subscribe(response => {
        if (response.missingArticles?.length) {
          this.missingArticles = response.missingArticles;
          return;
        }
        this.messageService.showSuccess();
        this.dialogService.complete(response);
      });
  }

  private onCategoryChanged(category: RentalObjectCategory): void {
    this.missingArticles = [];
    this.form.controls['rentalObjectTypeId'].setValue(null, { emitEvent: false });
    this.rentalObjectTypes = [];
    const needsPlacement = category === RentalObjectCategory.Dwelling || category === RentalObjectCategory.Premises;
    this.showBuilding = needsPlacement && !this.dataContext.buildingId;
    this.showEntrance = needsPlacement && !this.dataContext.entranceId;
    const buildingControl = this.form.controls['buildingId'];
    const entranceControl = this.form.controls['entranceId'];
    this.showBuilding ? buildingControl.setValidators(Validators.required) : buildingControl.clearValidators();
    this.showEntrance ? entranceControl.setValidators(Validators.required) : entranceControl.clearValidators();
    buildingControl.updateValueAndValidity({ emitEvent: false });
    entranceControl.updateValueAndValidity({ emitEvent: false });

    if (this.showBuilding && this.buildings.length === 0) {
      this.subscriptions.add(
        this.buildingService.getSelectionItemsByProperty(this.dataContext.propertyId).subscribe(items => (this.buildings = items))
      );
    }
    if (this.showEntrance && this.dataContext.buildingId) this.onBuildingChanged(this.dataContext.buildingId);
  }

  private onBuildingChanged(buildingId: string): void {
    this.entranceSubscription?.unsubscribe();
    if (!this.showEntrance || !buildingId) {
      this.entrances = [];
      if (!this.dataContext.entranceId) this.form.controls['entranceId'].setValue(null, { emitEvent: false });
      return;
    }
    this.form.controls['entranceId'].setValue(null, { emitEvent: false });
    this.entranceSubscription = this.entranceService
      .getList()
      .pipe(map(items => items.filter(item => item.building === buildingId).map(item => new SelectionItem(item.toString(), item.id))))
      .subscribe(items => (this.entrances = items));
    this.subscriptions.add(this.entranceSubscription);
  }
}
